import AssignmentIcon from '@mui/icons-material/Assignment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import CompositeDialog from '../dialog/CompositeDialog';
import { DialogTitleWithRecoveredNote } from './components/DialogTitleWithRecoveredNote';
import NoteField, { NoteFieldRef } from './NoteField';
import { isEmptyNoteHtml } from './hooks/useAutosave.hook';

import {
  CreateNoteForm,
  CreateNoteFormSchema,
  NoteDto,
  UpdateNoteForm,
  UpdateNoteFormSchema,
  UserDto,
  UserNeedDto,
} from '@act/shared/data-transfer-objects';
import { zodResolver } from '@hookform/resolvers/zod';
import { DialogFooterAction } from 'react-components/dialog/DialogFooter';
import { DatadogReplayPhi, DatadogReplayStatic } from '@act/storybook';
import { TrackingProvider } from 'react-components/tracking';
import { notesRequests } from 'redux/requests/notes.request';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import {
  createNoteSuccess,
  deleteNoteSuccess,
  updateNoteSuccess,
} from 'redux/slices/notesSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall, ErrorResponse } from 'redux/utils/apiHook.hook';
import { useToast } from '../app/ReusableToast';
import useConfirmationAlertDialog from './hooks/useConfirmationAlertDialog.hook';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { awaitFlushPendingNoteAutosave } from './flushPendingNoteAutosave.util';

export interface NoteDialogInterfaceProps {
  user: UserDto;
  note?: NoteDto;
  need?: UserNeedDto;
}

interface NoteDialogProps extends NoteDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

const NoteDialog = ({
  handleClose,
  mode,
  note,
  open,
  user,
}: NoteDialogProps) => {
  const isEdit = mode === 'Edit';
  const schema = isEdit ? UpdateNoteFormSchema : CreateNoteFormSchema;
  const { control, handleSubmit, reset, watch } = useForm<
    CreateNoteForm | UpdateNoteForm
  >({
    mode: 'onSubmit',
    defaultValues: {
      text: isEdit ? note?.text : '<p><br></p>',
      userId: user.id,
    },
    resolver: zodResolver(schema),
  });
  const dispatch = useAppDispatch();
  const toast = useToast();
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const canDelete = !!note?.id && note?.createdBy === actingUser.id;

  const noteFieldRef = useRef<NoteFieldRef>(null);
  const [showRecoveredNote, setShowRecoveredNote] = useState(false);

  const [updatedNote, isUpdatingNote, updateNote, updateNoteError] = useApiCall(
    notesRequests.updateNote
  );
  const [_deleteNoteResult, isDeleting, deleteNote, deleteNoteError] =
    useApiCall(notesRequests.deleteNote);
  const pendingDeleteNoteIdRef = useRef<number | null>(null);
  const deleteErrorAtStartRef = useRef<ErrorResponse | null>(null);
  const [createdNote, isLoadingCreate, createNote] = useApiCall(
    notesRequests.createNotes
  );
  const [manualSave, setManualSave] = useState(false);

  const isSubmitting = isLoadingCreate || isUpdatingNote || isDeleting;
  const text = watch('text');
  const savedTextBaselineRef = useRef(note?.text ?? '<p><br></p>');

  useEffect(() => {
    if (open) {
      savedTextBaselineRef.current = note?.text ?? '<p><br></p>';
    }
  }, [open, note?.id, note?.text]);

  useEffect(() => {
    if (updatedNote && !manualSave) {
      savedTextBaselineRef.current = updatedNote.text;
    }
  }, [updatedNote, manualSave]);

  const noteTextMatches = (
    left: string | undefined,
    right: string | undefined
  ): boolean => {
    if (left === right) {
      return true;
    }
    return isEmptyNoteHtml(left) && isEmptyNoteHtml(right);
  };

  const hasUnsavedChanges = (): boolean => {
    if (isEdit) {
      return !noteTextMatches(text, savedTextBaselineRef.current);
    }
    return !isEmptyNoteHtml(text);
  };

  // Edit autosaves through PATCH /notes/:id; the manual Save reuses the same
  // request and relies on `manualSave` to decide whether to close.
  const editSaveOverride = note?.id
    ? {
        onSave: (text: string) =>
          updateNote({
            params: { noteId: note.id },
            body: { noteId: note.id, text },
          }),
        isSaving: !!isUpdatingNote,
        error: updateNoteError,
      }
    : undefined;

  const onSubmit = async (data: CreateNoteForm | UpdateNoteForm) => {
    if (isEdit && !hasUnsavedChanges()) {
      handleClose();
      return;
    }

    try {
      if (isEdit && note) {
        await awaitFlushPendingNoteAutosave(noteFieldRef);
        setManualSave(true);
        updateNote({
          params: { noteId: note.id },
          body: { noteId: note.id, text: data.text },
        });
      } else {
        createNote({ body: { userId: user.id, text: data.text } });
        noteFieldRef.current?.clearDraft();
      }
    } catch (err) {
      console.error(`Error saving note: ${err}`);
    }
  };

  useEffect(() => {
    if (createdNote) {
      dispatch(createNoteSuccess(createdNote));
      toast('Note created successfully');
      reset();
      handleClose();
    }
    if (updatedNote && manualSave) {
      dispatch(updateNoteSuccess(updatedNote));
      toast('Note updated successfully');
      reset();
      handleClose();
    } else if (updatedNote && !manualSave) {
      setManualSave(false);
    }
  }, [createdNote, updatedNote]);

  useEffect(() => {
    if (pendingDeleteNoteIdRef.current === null || isDeleting) {
      return;
    }

    const noteId = pendingDeleteNoteIdRef.current;
    pendingDeleteNoteIdRef.current = null;

    if (deleteNoteError !== deleteErrorAtStartRef.current) {
      const errorMessage =
        deleteNoteError?.userMessage ||
        deleteNoteError?.message ||
        'Failed to delete note. Please try again.';
      toast(errorMessage, 'error');
      return;
    }

    dispatch(deleteNoteSuccess(noteId));
    reset();
    toast('Note deleted successfully');
    handleClose();
  }, [isDeleting, deleteNoteError, dispatch, reset, toast, handleClose]);

  async function handleDelete() {
    const confirmDelete = await openConfirmationAlertDialog({
      title: 'Delete note',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      message: 'This note will no longer be available',
      confirmationText: 'Yes, delete now',
      confirmButtonColor: 'error',
    });
    if (confirmDelete && note?.id) {
      deleteErrorAtStartRef.current = deleteNoteError;
      pendingDeleteNoteIdRef.current = note.id;
      deleteNote({ params: { noteId: note.id } });
    }
  }

  const actions: DialogFooterAction[] = [
    {
      text: 'Cancel',
      callback: () => handleFormClose(),
      variant: 'outlined',
      disabled: isSubmitting,
    },
    {
      text: 'Save',
      type: 'submit',
      variant: 'contained',
      disabled: isSubmitting,
    },
  ];

  if (canDelete) {
    actions.unshift({
      text: 'Delete',
      isLeft: true,
      callback: handleDelete,
      disabled: isSubmitting,
    });
  }

  const handleFormClose = () => {
    if (hasUnsavedChanges()) {
      void handleUnsavedChanges(() => {
        noteFieldRef.current?.clearDraft();
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  return (
    <TrackingProvider contextName="note-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        icon={<AssignmentIcon color="primary" style={{ marginRight: 8 }} />}
        title={
          <DialogTitleWithRecoveredNote
            title={
              <>
                <DatadogReplayStatic>
                  {mode === 'Create' ? 'New' : mode} Note for{' '}
                </DatadogReplayStatic>
                <DatadogReplayPhi>
                  {user?.firstName} {user?.lastName}
                </DatadogReplayPhi>
              </>
            }
            showRecoveredNote={showRecoveredNote}
          />
        }
        handleFormSubmit={handleSubmit(onSubmit)}
        actions={actions}
      >
        <NoteField
          ref={noteFieldRef}
          control={control}
          name="text"
          mode={isEdit ? 'edit' : 'create'}
          itemType="member"
          itemId={user.id}
          userId={user.id}
          draftType="notes"
          onRecoveredNoteChange={setShowRecoveredNote}
          showCharacterCount
          trackingName="note-text-input"
          save={isEdit ? editSaveOverride : undefined}
        />
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default NoteDialog;
